/**
 * FormatPresetBar Component
 *
 * Quick-select resolution presets for common platforms (YouTube, TikTok, Instagram).
 * Uses resolution values from the shared schema (no hardcoded values).
 */

import { memo } from 'react';
import { Monitor, Smartphone, Square, Youtube } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { LucideIcon } from 'lucide-react';

interface FormatPreset {
  id: string;
  labelKey: string;
  defaultLabel: string;
  resolution: string;
  icon: LucideIcon;
  aspect: string;
}

/**
 * Preset definitions using schema-valid resolution values.
 * Resolutions must match VideoGenerationRequest.resolution type.
 */
const FORMAT_PRESETS: FormatPreset[] = [
  {
    id: 'youtube',
    labelKey: 'scriptPreview.presetYouTube',
    defaultLabel: 'YouTube',
    resolution: '1920x1080',
    icon: Youtube,
    aspect: '16:9',
  },
  {
    id: 'tiktok',
    labelKey: 'scriptPreview.presetTikTok',
    defaultLabel: 'TikTok',
    resolution: '1080x1920',
    icon: Smartphone,
    aspect: '9:16',
  },
  {
    id: 'instagram',
    labelKey: 'scriptPreview.presetInstagram',
    defaultLabel: 'Instagram',
    resolution: '1080x1080',
    icon: Square,
    aspect: '1:1',
  },
  {
    id: 'hd',
    labelKey: 'scriptPreview.presetHD',
    defaultLabel: 'HD',
    resolution: '1280x720',
    icon: Monitor,
    aspect: '16:9',
  },
];

interface FormatPresetBarProps {
  value: string;
  onChange: (resolution: string) => void;
  disabled?: boolean;
}

export const FormatPresetBar = memo(function FormatPresetBar({
  value,
  onChange,
  disabled = false,
}: FormatPresetBarProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-secondary-400">
        {t('scriptPreview.format', { defaultValue: 'Format' })}
      </label>
      <div className="grid grid-cols-4 gap-1.5">
        {FORMAT_PRESETS.map((preset) => {
          const Icon = preset.icon;
          const isSelected = value === preset.resolution;

          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => onChange(preset.resolution)}
              disabled={disabled}
              className={`
                flex flex-col items-center gap-1.5 px-2 py-2.5 rounded-lg text-xs font-medium
                transition-all duration-200 min-h-[44px]
                ${
                  isSelected
                    ? 'bg-primary-600/15 text-primary-400 border border-primary-500/40 shadow-sm shadow-primary-500/10'
                    : 'bg-secondary-700/40 text-secondary-400 border border-transparent hover:bg-secondary-700/70 hover:text-secondary-200 hover:scale-[1.02]'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <Icon className={`h-4 w-4 ${isSelected ? 'text-primary-400' : ''}`} />
              <span className="leading-none">
                {t(preset.labelKey, { defaultValue: preset.defaultLabel })}
              </span>
              <span
                className={`text-[10px] leading-none px-1.5 py-0.5 rounded-full ${isSelected ? 'bg-primary-600/20 text-primary-400/80' : 'bg-secondary-600/50 text-secondary-500'}`}
              >
                {preset.aspect}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
});
